import { useAuth } from "../contexts/AuthContext";
import { useGuidance } from "../contexts/GuidanceContext";
import { useOrganisation } from "../contexts/OrganisationContext";
import { NetworkInvitationsBanner } from "../components/NetworkInvitationsBanner";
import { PendingInvitationsBanner } from "../components/PendingInvitationsBanner";

const formatDateUk = (date: Date): string => {
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

export function Dashboard() {
  const { user } = useAuth();
  const { organisation } = useOrganisation();
  const { isGuidanceActive } = useGuidance();

  return (
    <div className="space-y-6">
      <PendingInvitationsBanner />
      <NetworkInvitationsBanner />
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          {formatDateUk(new Date())}
        </p>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">
          Welcome{user?.fullName ? `, ${user.fullName}` : ""}
        </h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          You are looking at <span className="font-medium">{organisation?.name}</span>. The Vault,
          standards, nonconformities, audit log and supplier network are in the sidebar; Settings
          covers branding, members, billing and your personal preferences.
        </p>
      </div>

      {isGuidanceActive ? (
        <div
          role="region"
          aria-label="Coach guidance"
          className="rounded-lg border border-brand-200 bg-brand-50 p-4 text-sm text-brand-900 dark:border-brand-900/40 dark:bg-brand-500/10 dark:text-brand-100"
        >
          <p className="font-semibold">Coach is on.</p>
          <p className="mt-1">
            The Coach surfaces consultant-style guidance against each clause. Switch it off in
            Settings → Profile and preferences if you find it noisy; the choice persists across
            devices.
          </p>
        </div>
      ) : null}

      <SummaryGrid orgTier={organisation?.tier ?? null} />
    </div>
  );
}

function SummaryGrid({ orgTier }: { orgTier: string | null }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      <Card
        label="Active modules"
        value="See sidebar"
        footnote="Locked items show a tooltip; click one to open the upgrade flow."
      />
      <Card
        label="Tier"
        value={orgTier ?? "—"}
        footnote="Tier governs user limits, storage and module bundling."
      />
      <Card
        label="Audit chain"
        value="Hash-chained"
        footnote="Every sensitive action is appended to a tamper-evident log per organisation."
      />
    </div>
  );
}

function Card({ label, value, footnote }: { label: string; value: string; footnote: string }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </p>
      <p className="mt-1 text-lg font-semibold text-slate-900 dark:text-slate-100">{value}</p>
      <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{footnote}</p>
    </div>
  );
}
